/**
 * The API client. Every call sends the Kinde access token as a Bearer and parses the response with zod.
 * Errors become ApiError with the API's own message, so a 403 or 409 can be shown as the API worded it.
 */
import type { z } from 'zod'
import {
  ActionResult,
  AdminCounts,
  AlertDetail,
  AlertEnvelope,
  AlertList,
  DeliveryEnvelope,
  DeliveryList,
  Ok,
  WebhookCreated,
  TokenCreated,
  TokenList,
  type CreateTokenInput,
  WebhookEnvelope,
  WebhookList,
  type AlertQuery,
  type CreateWebhookInput,
  ClaudeCodeResult,
  CoachingView,
  CoachingDeleted,
  CoachingDeletedOnBehalf,
  CoachingOffBody,
  CoachingSessionRead,
  CoachingSessions,
  type CoachingRead,
  GrantCreated,
  GrantList,
  GrantRevoked,
  ShareScreens,
  SharedWithMe,
  type CreateGrantInput,
  Diagnosis,
  ClaudeLogin,
  ClaudeStatus,
  CodexLogin,
  CodexStatus,
  PersonSetup,
  PeopleList,
  InviteResult,
  type InviteInput,
  type AgentName,
  type PersonStepKey,
  Connections,
  ErrorBody,
  FounderWorld,
  Fleet,
  Pairing,
  RotateResult,
  ProvisionResult,
  ShopifyConnectResult,
  TripleWhaleConnectResult,
  FounderDetail,
  FounderEnvelope,
  FounderList,
  Me,
  PersonEnvelope,
  Plans,
  ProgressEnvelope,
  RunEnvelope,
  RunWithActive,
  Sizes,
  Secrets,
  Waitlist,
  WaitlistJoin,
  type CreateFounderInput,
  type CreatePersonInput,
  type ActionKey,
  type CreateRunInput,
  type SetSecretInput,
  type ShopifyConnectInput,
  type TripleWhaleConnectInput,
  type StepAction,
  AdminUsage,
  FounderUsage,
} from '../domain/schemas'
import type { GeoKey } from '../domain/plans'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly issues?: { path: string; message: string }[],
    readonly email?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ApiOptions {
  baseUrl: string
  getToken: () => Promise<string | undefined>
  fetch?: typeof fetch
}

/** Turns a failed response into an ApiError, keeping the API's `error`, `code` and `issues`. */
export async function toApiError(res: Response): Promise<ApiError> {
  let raw: unknown = null
  try {
    raw = await res.json()
  } catch {
    // not JSON: fall through to the status text
  }
  const body = ErrorBody.safeParse(raw)
  if (!body.success) return new ApiError(res.status, res.statusText || `request failed (${res.status})`)
  const { error, code, issues, email } = body.data
  const detail = issues?.length ? `${error}: ${issues.map((i) => (i.path ? `${i.path} ${i.message}` : i.message)).join('; ')}` : error
  return new ApiError(res.status, detail, code, issues, email)
}

export function createApi(opts: ApiOptions) {
  const base = opts.baseUrl.replace(/\/+$/, '')
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a))

  async function send(path: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers)
    const token = await opts.getToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (init.body !== undefined && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
    return doFetch(`${base}${path}`, { ...init, headers })
  }

  async function call<T extends z.ZodType>(schema: T, path: string, init?: RequestInit): Promise<z.infer<T>> {
    const res = await send(path, init)
    if (!res.ok) throw await toApiError(res)
    return parse(schema, res, path)
  }

  async function parse<T extends z.ZodType>(schema: T, res: Response, path: string): Promise<z.infer<T>> {
    const json: unknown = await res.json()
    const parsed = schema.safeParse(json)
    if (!parsed.success) {
      throw new ApiError(res.status, `unexpected response from ${path}: ${parsed.error.issues[0]?.path.join('.') ?? ''} ${parsed.error.issues[0]?.message ?? ''}`.trim(), 'bad_shape')
    }
    return parsed.data
  }

  /**
   * An admin coaching read: the switch off (403 `coaching_off`, naming the founder) and no store on this server (503
   * `unavailable`) are answers the page draws, not errors. Anything else that fails throws as usual.
   */
  async function coachingRead<T extends z.ZodType>(schema: T, path: string): Promise<CoachingRead<z.infer<T>>> {
    const res = await send(path)
    if (res.status === 403 || res.status === 503) {
      const raw: unknown = await res.clone().json().catch(() => null)
      const off = CoachingOffBody.safeParse(raw)
      if (res.status === 403 && off.success) return { state: 'off', founder: off.data.founder, coaching: off.data.coaching }
      const err = await toApiError(res)
      if (res.status === 503 && err.code === 'unavailable') return { state: 'unavailable', reason: err.message }
      throw err
    }
    if (!res.ok) throw await toApiError(res)
    return { state: 'on', data: await parse(schema, res, path) }
  }

  const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })
  const enc = encodeURIComponent

  return {
    /** Any GET, parsed with the caller's schema: what a section's data.ts uses (bun run new:section). */
    get: <T extends z.ZodType>(path: string, schema: T) => call(schema, path),
    me: () => call(Me, '/api/me'),
    /** The caller's setup marks; each is set once and kept. */
    setProgress: async (marks: { setupComplete?: true; paired?: true }) => (await call(ProgressEnvelope, '/api/me/progress', post(marks))).progress,
    /** Clears both marks, so setup runs again. */
    resetProgress: async () => (await call(ProgressEnvelope, '/api/me/progress/reset', { method: 'POST' })).progress,
    /** The caller's person setup: steps, how each is done, their agent, completion. */
    mySetup: () => call(PersonSetup, '/api/me/setup'),
    /** Marks a step done or skipped by the person, or opens it again. */
    markStep: (key: Exclude<PersonStepKey, 'signin'>, status: 'done' | 'skipped' | 'open') => call(PersonSetup, `/api/me/setup/${key}`, post({ status })),
    completeSetup: () => call(PersonSetup, '/api/me/setup/complete', { method: 'POST' }),
    /** "Run setup again": clears the person's marks; nothing real is undone. */
    rerunSetup: () => call(PersonSetup, '/api/me/setup/reset', { method: 'POST' }),
    setAgent: (agent: AgentName) => call(PersonSetup, '/api/me/agent', { method: 'PUT', body: JSON.stringify({ agent }) }),
    /** The caller's personal MCP tokens and the apps they may make one for. */
    myTokens: () => call(TokenList, '/api/me/tokens'),
    /** The value comes back here once and never again. */
    createToken: (input: CreateTokenInput) => call(TokenCreated, '/api/me/tokens', post(input)),
    revokeToken: (id: string) => call(Ok, `/api/me/tokens/${enc(id)}/revoke`, { method: 'POST' }),
    /** For a signed-in person who isn't invited: records them once (a repeat is fine). */
    joinWaitlist: () => call(WaitlistJoin, '/api/waitlist', { method: 'POST' }),
    waitlist: async () => (await call(Waitlist, '/api/waitlist')).waitlist,
    /** The nav badges in one call (admins). An API from before it answers 404; the shell then counts the old way. */
    counts: () => call(AdminCounts, '/api/admin/counts'),
    founders: async () => (await call(FounderList, '/api/founders')).founders,
    founder: (id: string) => call(FounderDetail, `/api/founders/${enc(id)}`),
    createFounder: async (input: CreateFounderInput) => (await call(FounderEnvelope, '/api/founders', post(input))).founder,
    addPerson: async (input: CreatePersonInput) => (await call(PersonEnvelope, '/api/people', post(input))).person,
    createRun: async (input: CreateRunInput) => (await call(RunEnvelope, '/api/runs', post(input))).run,
    run: (id: string) => call(RunWithActive, `/api/runs/${enc(id)}`),
    stepAction: async (runId: string, key: string, action: StepAction) =>
      (await call(RunEnvelope, `/api/runs/${enc(runId)}/steps/${enc(key)}/${action}`, { method: 'POST' })).run,
    sizes: () => call(Sizes, '/api/vendors/sizes'),
    plans: () => call(Plans, '/api/vendors/plans'),
    /** Every live box's agent health; `founderId` narrows to one founder. */
    fleet: (founderId?: string) => call(Fleet, founderId ? `/api/fleet?founderId=${enc(founderId)}` : '/api/fleet'),
    /** One box's lights, findings, actions and recent actions; `fresh` asks the vendors again instead of the 30 s cache. */
    diagnosis: (boxId: string, fresh = false) => call(Diagnosis, `/api/fleet/${enc(boxId)}/diagnosis${fresh ? '?fresh=1' : ''}`),
    /** One restart or redeploy. Audited with the admin's email; one per box every 30 s (429 otherwise). */
    boxAction: (boxId: string, action: ActionKey) => call(ActionResult, `/api/fleet/${enc(boxId)}/actions/${action}`, { method: 'POST' }),
    /** Alerts, open first. */
    alerts: (q: AlertQuery = {}) => {
      const p = new URLSearchParams(Object.entries(q).flatMap(([k, v]) => (v === undefined ? [] : [[k, String(v)]])))
      return call(AlertList, `/api/alerts${p.size ? `?${p}` : ''}`)
    },
    alert: (id: string) => call(AlertDetail, `/api/alerts/${enc(id)}`),
    acknowledgeAlert: async (id: string) => (await call(AlertEnvelope, `/api/alerts/${enc(id)}/acknowledge`, { method: 'POST' })).alert,
    resolveAlert: async (id: string) => (await call(AlertEnvelope, `/api/alerts/${enc(id)}/resolve`, { method: 'POST' })).alert,
    webhooks: () => call(WebhookList, '/api/webhooks'),
    /** The secret comes back here once and never again. */
    createWebhook: (input: CreateWebhookInput) => call(WebhookCreated, '/api/webhooks', post(input)),
    updateWebhook: async (id: string, patch: { url?: string; active?: boolean; description?: string | null }) =>
      (await call(WebhookEnvelope, `/api/webhooks/${enc(id)}`, { method: 'PATCH', body: JSON.stringify(patch) })).webhook,
    deleteWebhook: (id: string) => call(Ok, `/api/webhooks/${enc(id)}`, { method: 'DELETE' }),
    testWebhook: async (id: string) => (await call(DeliveryEnvelope, `/api/webhooks/${enc(id)}/test`, { method: 'POST' })).delivery,
    deliveries: async (id: string) => (await call(DeliveryList, `/api/webhooks/${enc(id)}/deliveries`)).deliveries,
    /** The raw SSE response. EventSource can't send headers, so the stream is read with fetch. */
    runEvents: (id: string, signal: AbortSignal) => send(`/api/runs/${enc(id)}/events`, { headers: { Accept: 'text/event-stream' }, signal }),

    // The founder side (docs/founder-api.md): always the caller's own founder.
    myFounder: () => call(FounderWorld, '/api/founder'),
    founderEvents: (signal: AbortSignal) => send('/api/founder/events', { headers: { Accept: 'text/event-stream' }, signal }),
    provision: async (geo: GeoKey) => (await call(ProvisionResult, '/api/founder/provision', post({ geo }))).run,
    pairing: () => call(Pairing, '/api/founder/pairing', { method: 'POST' }),
    /** "Remove all devices": re-keys the Linux user's Paseo. Founders and teammates their own; admins name any user. */
    rotatePairing: (user?: string, founderId?: string) =>
      call(RotateResult, `/api/founder/pairing/rotate${founderId ? `?founderId=${enc(founderId)}` : ''}`, post(user ? { user } : {})),
    claudeLogin: () => call(ClaudeLogin, '/api/founder/claude/login', { method: 'POST' }),
    claudeCode: (sessionId: string, code: string) => call(ClaudeCodeResult, `/api/founder/claude/login/${enc(sessionId)}/code`, post({ code })),
    claudeStatus: () => call(ClaudeStatus, '/api/founder/claude/status'),
    codexLogin: () => call(CodexLogin, '/api/founder/codex/login', { method: 'POST' }),
    codexStatus: () => call(CodexStatus, '/api/founder/codex/status'),
    /** The founder's people with their account and setup progress. Admins pass the founder. */
    people: (founderId?: string) => call(PeopleList, `/api/founder/people${founderId ? `?founderId=${enc(founderId)}` : ''}`),
    /** Founders (and admins) invite a teammate: an Add person run starts. */
    invite: (input: InviteInput, founderId?: string) => call(InviteResult, `/api/founder/people${founderId ? `?founderId=${enc(founderId)}` : ''}`, post(input)),
    connections: async () => (await call(Connections, '/api/founder/connections')).connections,
    connectShopify: (input: ShopifyConnectInput) => call(ShopifyConnectResult, '/api/founder/connections/shopify', post(input)),
    /** Check again with the saved keys, after a new version is released in Shopify. */
    recheckShopify: () => call(ShopifyConnectResult, '/api/founder/connections/shopify/check', post({})),
    connectTripleWhale: (input: TripleWhaleConnectInput) => call(TripleWhaleConnectResult, '/api/founder/connections/triplewhale', post(input)),
    /** Removes a connection's keys from the store, the computer and the live app. */
    disconnect: (key: string) => call(Ok, `/api/founder/connections/${enc(key)}`, { method: 'DELETE' }),
    /** Names, notes and times; never a value. */
    secrets: async () => (await call(Secrets, '/api/founder/secrets')).secrets,
    /** Write-only. A value with no note keeps the stored note; a note alone changes only the note. */
    setSecret: (name: string, input: SetSecretInput) => call(Ok, `/api/founder/secrets/${enc(name)}`, { method: 'PUT', body: JSON.stringify(input) }),
    removeSecret: (name: string) => call(Ok, `/api/founder/secrets/${enc(name)}`, { method: 'DELETE' }),
    /** Coding usage this week in the person's time zone; admins pass the founder (board usage-r1). */
    usage: (founderId?: string, tz = Intl.DateTimeFormat().resolvedOptions().timeZone) =>
      call(FounderUsage, `/api/founder/usage?tz=${enc(tz)}${founderId ? `&founderId=${enc(founderId)}` : ''}`),
    /** Admin only: per founder, the last 7 days, per Linux user, and the stuck and idle signals. */
    adminUsage: () => call(AdminUsage, '/api/admin/usage'),
    /** The coaching switch with its record. Admins pass the founder; founders and teammates read their own. */
    coaching: (founderId?: string) => call(CoachingView, `/api/founder/coaching${founderId ? `?founderId=${enc(founderId)}` : ''}`),
    /** Only a founder turns it on; founders, teammates and admins turn it off. Asking for the state it's in changes nothing. */
    setCoaching: (on: boolean, founderId?: string) =>
      call(CoachingView, `/api/founder/coaching${founderId ? `?founderId=${enc(founderId)}` : ''}`, { method: 'PUT', body: JSON.stringify({ on }) }),
    /** Only the founder: deletes every conversation cpg.computer received, at once. Answers what went and the view. */
    deleteCoachingData: () => call(CoachingDeleted, '/api/founder/coaching/data', { method: 'DELETE' }),
    /** Admin, as the founder's hand: deletes what we received, with the reason naming the founder's request. */
    deleteCoachingDataFor: (founderId: string, reason: string) =>
      call(CoachingDeletedOnBehalf, `/api/coaching/${enc(founderId)}/data`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
    /** Admin: a founder's conversations, newest activity first, while their coaching is on. */
    coachingSessions: (founderId: string) => coachingRead(CoachingSessions, `/api/coaching/${enc(founderId)}/sessions`),
    /** Admin: one conversation in written order, while coaching is on. The API audits the read. */
    coachingSession: (founderId: string, sessionId: string) => coachingRead(CoachingSessionRead, `/api/coaching/${enc(founderId)}/sessions/${enc(sessionId)}`),
    /** The live app's shareable screens, read through its credential; the last list when it doesn't answer. */
    shareScreens: () => call(ShareScreens, '/api/founder/screens'),
    /** Who sees which screens: unrevoked grants and what the caller may do. */
    grants: () => call(GrantList, '/api/founder/grants'),
    /** Only a founder shares. Sharing the same screen with the same person again moves its end date. */
    createGrant: (input: CreateGrantInput) => call(GrantCreated, '/api/founder/grants', post(input)),
    revokeGrant: (id: string) => call(GrantRevoked, `/api/founder/grants/${enc(id)}/revoke`, { method: 'POST' }),
    /** What is shared with the signed-in person, per founder app. */
    sharedWithMe: () => call(SharedWithMe, '/api/me/shared'),
  }
}

export type Api = ReturnType<typeof createApi>
